<script>
import { computedPbxBasePlans, computedPbxPlans, PBX_SUB_TYPES, computedPbxPhoneNumberPlans } from '@/common/service/computers/plans/pbx';
import mixin from '@/plans/mx/shopping-cart';
import { plansof } from '@/utils/chain';
import { mapGetters } from 'vuex';
import { planTypes } from '@/common/constants';
import { logger } from '@/utils/log';
import AltProratedPlanUser from '@/views/config/shopping-cart/prorated/plan-user.vue';
import AltProratedPlanPrice from '@/views/config/shopping-cart/prorated/plan-price.vue';
import AltProratedPlanPeriod from '@/views/config/shopping-cart/prorated/plan-period.vue';
import AltProratedPlanMultiple from '@/views/config/shopping-cart/prorated/multiple.vue';

export default {
  mixins: [mixin],
  components: {
    AltProratedPlanUser,
    AltProratedPlanPrice,
    AltProratedPlanPeriod,
    AltProratedPlanMultiple,
  },
  computed: {
    ...mapGetters(['getSelectedPlans']),
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    pbxAddonPlans() {
      return this.selectedPbxPlans.filter(item => [PBX_SUB_TYPES.FEATURE, PBX_SUB_TYPES.UNLIMITED_ADDON].includes(item.sub_type));
    },
    hasPbxCart() {
      return this.filterKeys.includes('ZOOM_PHONE');
    },
  },
  methods: {
    $renderCallingPlanContent(plan) {
      const { parent_category, category } = plan;
      const pbxCategoryCode = category.toUpperCase().replace(parent_category.toUpperCase(), '');
      let cartName = this.$t(`billing.pbx.cart_name.${pbxCategoryCode}`);
      if (pbxCategoryCode === 'ZSU') {
        cartName = this.$t('billing.pbx.plan_name.ZSU');
      }
      return (
        <AltProratedPlanMultiple code={plan.plan_code}>
        <div class="pbx-cart-part">
          {this.mx_subTitle(
            cartName,
            <AltProratedPlanPrice code={plan.plan_code}>{this.$money(this.mx_price(plan))}</AltProratedPlanPrice>,
            plan.plan_code
          )}
          {this.mx_discount(plan.plan_code)}
          <AltProratedPlanUser code={plan.plan_code}>
          {this.mx_license(plan.cartInfo.selected_number, this.$t('billing.planform.unit.user'))}
          </AltProratedPlanUser>
          {this.mx_cycle(plan.billing_cycle)}
          <AltProratedPlanPeriod code={plan.plan_code} />
          {this.mx_edit(plan)}
        </div>
        </AltProratedPlanMultiple>
      );
    },
    $renderCallingPlan() {
      const callingPlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      if (!callingPlans.length) return;
      return callingPlans.map((plan) => this.$renderCallingPlanContent(plan));
    },
    $renderLocalNumberPlan() {
      const phoneNumberPlans = computedPbxPhoneNumberPlans(this.getSelectedPlans);
      if (!phoneNumberPlans.length) return;
      return phoneNumberPlans.map((plan) => {
        return (
          <AltProratedPlanMultiple code={plan.plan_code}>
            <div class="pbx-cart-part">
              {this.mx_subTitle(
                this.$t(`billing.pbx.cart_name.${plan.category}`),
                <AltProratedPlanPrice code={plan.plan_code}>{this.$money(this.mx_price(plan))}</AltProratedPlanPrice>,
                plan?.plan_code
              )}
              {this.mx_discount(plan.plan_code)}
              <AltProratedPlanUser code={plan.plan_code}>
              {this.mx_license(plan.cartInfo.selected_number, this.$t('billing.planform.unit.number'))}
              </AltProratedPlanUser>
              {this.mx_cycle(plan.billing_cycle)}
              <AltProratedPlanPeriod code={plan.plan_code} />
              {this.mx_edit(plan)}
            </div>
          </AltProratedPlanMultiple>
        );
      });
    },
    $renderPbxBasePlan() {
      const pbxBasePlan = computedPbxBasePlans(this.getSelectedPlans)[0];
      if (!pbxBasePlan) return null;
      if (this.hasPbxCart) return null;
      return (
        <AltProratedPlanMultiple code={pbxBasePlan.plan_code}>
        <div class="pbx-cart-part">
          {this.mx_subTitle(this.$t('billing.pbx.calling_fee_name.pay_you_go.MC'), <AltProratedPlanPrice code={pbxBasePlan.plan_code}>{this.$money(pbxBasePlan.amount)}</AltProratedPlanPrice>, pbxBasePlan.plan_code)}
          {this.mx_discount(pbxBasePlan.plan_code)}
          {this.mx_cycle(pbxBasePlan.billing_cycle)}
          <AltProratedPlanPeriod code={pbxBasePlan.plan_code} />
          {this.mx_edit(pbxBasePlan)}
        </div>
        </AltProratedPlanMultiple>
      );
    },
    $renderPbxAddons() {
      if (!this.pbxAddonPlans?.length) return null;
      if (this.hasPbxCart) return null;

      logger.info("render pbx addon plan in pro plus context");

      return <div class="opc-cart-item">
        <h1 class="opc-cart-item__title text-capitalize">{this.$t('billing.planform.title.zoomphone')}</h1>
        {
          this.pbxAddonPlans.map(item => (
            <AltProratedPlanMultiple code={item.plan_code} key={item.plan_code}>
            <div class="pbx-cart-part" key={item.plan_code}>
              {this.mx_subTitle(this.$t(`billing.pbx.plan_name.${item.category}`), <AltProratedPlanPrice code={item.plan_code}>{this.$money(this.mx_price(item))}</AltProratedPlanPrice>, item.plan_code)}
              {this.mx_discount(item.plan_code)}
              <AltProratedPlanUser code={item.plan_code}>
              {this.mx_license(item.cartInfo.selected_number, this.$t('billing.planform.unit.number'))}
              </AltProratedPlanUser>
              {this.mx_cycle(item.billing_cycle)}
              <AltProratedPlanPeriod code={item.plan_code} />
              {this.mx_edit(item)}
              {this.isShowRmvBtn && this.mx_remove(item)}
            </div>
            </AltProratedPlanMultiple>
          ))
        }
      </div>;
    },
    mergePlans (planList) {
      if(!Array.isArray(planList)) {
        return [];
      }
      return planList.reduce((acc, cur) => {
        if(Array.isArray(cur)) {
          return [...acc, ...cur];
        }
      }, [])
    },

    renderMainTitle () {
      const callingPlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
      const pbxBasePlan = computedPbxBasePlans(this.getSelectedPlans);
      const plans = this.mergePlans([callingPlans, pbxBasePlan]);
      return plans.map((item, index) => {
        if(!index) {
          return  this.mx_main_title(this.$t('billing.planform.title.zoom_one_biz_plus'), undefined, item.parent_category)
        }
        return <div style='display: none;'>{this.mx_main_title(this.$t('billing.planform.title.zoom_one_biz_plus'), undefined, item.parent_category)}</div>
      })
    },

    $renderPrice() {
      return (
        <div>
          <div class="opc-cart-item">
            {this.renderMainTitle()}
            {this.$renderCallingPlan()}
            {this.$renderLocalNumberPlan()}
            {this.$renderPbxBasePlan()}
          </div>
          {this.$renderPbxAddons()}
        </div>
      );
    },
  },
};
</script>
<style lang="scss">
.pbx-cart-part {
  margin-bottom: 10px;
}
</style>
